import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import AlertDialog from '../AlertDialog';
import { useAlertDialog } from '../../hooks/useAlertDialog';

interface HomepageImage {
  id: string;
  image_url: string;
  top_pos: string | null;
  left_pos: string | null;
  right_pos: string | null;
  bottom_pos: string | null;
  rotation: string;
  width_val: string;
  height_val: string;
  z_index_val: number;
}

interface EditHomepageImageModalProps {
  image: HomepageImage;
  onClose: () => void;
  onSubmit: (id: string, data: Partial<HomepageImage>) => Promise<void>;
}

const EditHomepageImageModal: React.FC<EditHomepageImageModalProps> = ({
  image,
  onClose,
  onSubmit
}) => {
  const [topPos, setTopPos] = useState(image.top_pos || '');
  const [leftPos, setLeftPos] = useState(image.left_pos || '');
  const [rightPos, setRightPos] = useState(image.right_pos || '');
  const [bottomPos, setBottomPos] = useState(image.bottom_pos || '');
  const [rotation, setRotation] = useState(image.rotation);
  const [width, setWidth] = useState(image.width_val);
  const [height, setHeight] = useState(image.height_val);
  const [zIndex, setZIndex] = useState(image.z_index_val);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isOpen, title, message, setAlert, clearAlert } = useAlertDialog();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await onSubmit(image.id, {
        top_pos: topPos || null,
        left_pos: leftPos || null,
        right_pos: rightPos || null,
        bottom_pos: bottomPos || null,
        rotation,
        width_val: width,
        height_val: height,
        z_index_val: zIndex
      });
      onClose();
    } catch (error) {
      console.error('Error updating image:', error);
      setAlert('Erro', 'Falha ao atualizar imagem. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div 
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
        >
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-xl font-playfair font-medium text-wedding-blue-800">
              Edit Image Position
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <img
                  src={image.image_url}
                  alt="Preview"
                  className="w-full h-48 object-contain rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Top Position
                  </label>
                  <input
                    type="text"
                    value={topPos}
                    onChange={(e) => setTopPos(e.target.value)}
                    placeholder="e.g., 10% or 50vh"
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Left Position
                  </label>
                  <input
                    type="text"
                    value={leftPos}
                    onChange={(e) => setLeftPos(e.target.value)}
                    placeholder="e.g., 10% or 50vw"
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Right Position
                  </label>
                  <input
                    type="text"
                    value={rightPos}
                    onChange={(e) => setRightPos(e.target.value)}
                    placeholder="e.g., 10% or 50vw"
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bottom Position
                  </label>
                  <input
                    type="text"
                    value={bottomPos}
                    onChange={(e) => setBottomPos(e.target.value)}
                    placeholder="e.g., 10% or 50vh"
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rotation
                </label>
                <input
                  type="text"
                  value={rotation}
                  onChange={(e) => setRotation(e.target.value)}
                  placeholder="e.g., -5deg"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Width
                </label>
                <input
                  type="text"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  placeholder="e.g., 50vw"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Height
                </label>
                <input
                  type="text"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="e.g., auto"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Z-Index
                </label>
                <input
                  type="number"
                  value={zIndex}
                  onChange={(e) => setZIndex(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-wedding-blue-600 text-white rounded-md hover:bg-wedding-blue-700 transition-colors disabled:bg-gray-400"
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>

      <AlertDialog
        isOpen={isOpen}
        title={title}
        message={message}
        onClose={clearAlert}
      />
    </>
  );
};

export default EditHomepageImageModal;